import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { ALL_COUNTRIES } from "@/data/destinations";
import { LANGS, useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { lang, setLang, t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", destination: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, destination: form.destination || null, language: lang });
      toast.success(t("form.success"));
      setForm({ name: "", email: "", destination: "", message: "" });
    } catch {
      toast.error(t("form.error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border-b border-white/20 bg-transparent py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-[#d4af37]";

  return (
    <footer id="concierge" className="relative border-t border-white/10 bg-[#050814] pt-28 lg:pt-36" data-testid="footer">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <p className="eyebrow mb-6">{t("form.eyebrow")}</p>
            <h2 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl lg:text-6xl">
              {t("form.title")}
            </h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-white/60">{t("form.sub")}</p>
            <div className="mt-12">
              <div className="font-serif text-3xl font-light italic text-[#e8cf7a]">{t("footer.tagline")}</div>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
                {t("footer.langNote")}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="space-y-7" data-testid="lead-form">
              <div className="grid gap-7 sm:grid-cols-2">
                <input
                  data-testid="lead-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("form.name")}
                  className={inputCls}
                />
                <input
                  data-testid="lead-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t("form.email")}
                  className={inputCls}
                />
              </div>
              <select
                data-testid="lead-destination-select"
                value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })}
                className={`${inputCls} appearance-none ${form.destination ? "text-white" : "text-white/35"}`}
              >
                <option value="" className="bg-[#0a1128]">{t("form.anywhere")}</option>
                {ALL_COUNTRIES.map((c) => (
                  <option key={c.slug} value={c.name} className="bg-[#0a1128]">{c.name}</option>
                ))}
              </select>
              <textarea
                data-testid="lead-message-input"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t("form.message")}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="lead-submit-button"
                disabled={sending}
                className="btn-gold group flex items-center gap-3 px-8 py-4 text-sm font-semibold tracking-wide disabled:opacity-60"
              >
                {sending ? t("form.sending") : t("form.submit")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-8 border-t border-white/10 pt-10 sm:grid-cols-3" data-testid="footer-contact">
          <a href="tel:+905435189650" data-testid="footer-phone" className="group flex items-start gap-4">
            <Phone size={16} className="mt-1 shrink-0 text-[#d4af37]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{t("footer.contact")}</div>
              <div className="mt-1 text-sm text-white/80 transition-colors group-hover:text-white">+90 543 518 96 50</div>
            </div>
          </a>
          <a href="mailto:support@aureviatravella.com" data-testid="footer-email" className="group flex items-start gap-4">
            <Mail size={16} className="mt-1 shrink-0 text-[#d4af37]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">E-mail</div>
              <div className="mt-1 text-sm text-white/80 transition-colors group-hover:text-white">support@aureviatravella.com</div>
            </div>
          </a>
          <div className="flex items-start gap-4" data-testid="footer-address">
            <MapPin size={16} className="mt-1 shrink-0 text-[#d4af37]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">London, UK</div>
              <div className="mt-1 text-sm leading-relaxed text-white/80">
                102 Rookery Court, 80 Ruckholt Road, Mainyard Studios, Office C05, Suite G1276, London, E10 5FA, United Kingdom
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-8 border-t border-white/10 py-10 lg:flex-row lg:items-center">
          <Link to="/" className="flex items-baseline gap-2" data-testid="footer-logo">
            <span className="font-serif text-xl font-medium text-white">Aurevia</span>
            <span className="font-mono text-[9px] tracking-[0.4em] text-[#d4af37]">TRAVELLA</span>
          </Link>
          <div className="flex flex-wrap gap-2" data-testid="footer-langs">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`footer-lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`border px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] transition-colors ${lang === l.code ? "border-[#d4af37] text-[#d4af37]" : "border-white/15 text-white/50 hover:border-white/40 hover:text-white"}`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <Link to="/privacy" data-testid="footer-privacy-link" className="gold-underline font-mono text-[10px] tracking-[0.2em] text-white/50 hover:text-[#e8cf7a]">
            {t("footer.privacy")}
          </Link>
          <p className="font-mono text-[10px] tracking-[0.2em] text-white/35" data-testid="footer-rights">
            {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}
